import React, {
  useState,
  Fragment,
  useEffect,
  memo,
  useMemo,
  useCallback,
} from 'react';
import MediaQuery from 'react-responsive';
import { useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import cn from 'classnames';

import {
  WEIGHT_METRIC,
  HEIGHT_METRIC,
  TEMPERATURE_METRIC,
  GLUCOSE_METRIC,
  FEATURE_TOGGLE_CONFIG,
} from '../../constants';
import { BREAKPOINT } from '../../constants/styles';

import MobileNavbar from '../common/MobileNavbar';
import BackButton from '../common/BackButton';
import ContentArea from '../ContentArea';
import Popup from '../common/Popup';
import Drawer from '../Drawer';
import { TABS_PATIENT_PORTAL, VITALS_ACTIVITY_TYPE } from './constants';
import { useDataListEntity } from './PatientPortal/useDataListEntity';
import { useCheckForAvailableData } from './useCheckForAvailableData';
import { useActionFilters } from './Chart/Filters/useActionFilters';
import SinglePointValueModal from './Chart/SinglePointValueModal';
import { useFeatureToggleConfig } from './useFeatureToggleConfig';
import { SaturationInfoForm } from './Forms/SaturationInfoForm';
import DataListWrapper from './PatientPortal/DataListWrapper';
import { consts as popupConsts } from '../../redux/ui/popup';
import { AddBodyInfoForm } from './Forms/AddBodyInfoForm';
import { UI_POPUP_TYPES } from '../../constants/props';
import { AddWeightForm } from './Forms/AddWeightForm';
import { AddHeightForm } from './Forms/AddHeightForm';
import { useTabs } from './PatientPortal/useTabsData';
import SingleDayGrafModal from './SingleDayGrafModal';
import { usePageView } from '../../hocs/usePageView';
import TimeScalableChart from './TimeScalableChart';
import DataHistoricList from './DataHistoricList';
import { VitalsActivity } from './VitalsActivity';
import MetaHelmet from '../AppHelmet/MetaHelmet';
import { EmptyState } from './EmptyState';
import AddDataModal from './AddDataModal';
import Navigation from './Navigation';
import Chart from './Chart';
import Icon from '../Icon';
import Button from '../Button';
import { enhance } from './enhance';
import styles from './index.styl';

const {
  WEIGHT,
  SATURATION,
  HEIGHT,
  BMI,
  BLOOD_PRESSURE,
  RESPIRATORY_RATE,
  BODY_SURFACE_AREA,
  BODY_TEMPERATURE,
} = VITALS_ACTIVITY_TYPE;

const { VITALS_AND_ACTIVITY, MEDICATIONS } = TABS_PATIENT_PORTAL;

function PatientHealthboard({
  profileTargetUser,
  profile,
  intl: { formatMessage },
  isProvider,
  vitalsActivityData,
  activeCardType,
  setActiveCardType,
  activeFilter,
  setActiveFilter,
  drawerVisibility: { visible, desktopVisible },
  setTargetUserId,
  deepLinkTab,
  deepLinkDependentId,
  clearHistoricData,
  availableCategoriesPending,
  refillsHistory,
}) {
  usePageView('Healthboard');
  useEffect(() => {
    if (deepLinkTab && deepLinkDependentId) {
      setTargetUserId(parseInt(deepLinkDependentId, 10));
    }
  }, []);

  const { displayableName, id, settings } = useMemo(
    () => (isEmpty(profileTargetUser) ? profile : profileTargetUser),
    [profileTargetUser, profile],
  );

  const mainUser = id === profile.id;
  const { height, weight, temperature, glucose } = get(
    settings,
    'measurementUnitsSettings',
    {},
  );

  const [isDetailsView, toggleDetailsView] = useState(false);
  const [opened, toggleOpenModal] = useState(false);
  const isWeightType = WEIGHT === activeCardType;
  const isHeightType = HEIGHT === activeCardType;
  const isSaturationType = SATURATION === activeCardType;
  const isHeightInMeters = height === HEIGHT_METRIC.METERS;
  const isWeightInKg = weight === WEIGHT_METRIC.KG;
  const isMilligramsPerDeciliter = glucose === GLUCOSE_METRIC.MG_PER_DECILITER;
  const isTemperatureInCelsius = temperature === TEMPERATURE_METRIC.CELSIUS;
  const isTimeScalableChart = useMemo(
    () =>
      [
        BLOOD_PRESSURE,
        RESPIRATORY_RATE,
        BODY_SURFACE_AREA,
        BODY_TEMPERATURE,
      ].includes(activeCardType),
    [activeCardType],
  );

  const { getFeatureConfig, isFeatureToggleOn } = useFeatureToggleConfig({
    feature: FEATURE_TOGGLE_CONFIG.MEDICATION_REFILL,
    options: { userId: id },
  });

  const isAvailableData = useCheckForAvailableData(
    vitalsActivityData,
    activeCardType,
  );

  const {
    tabs,
    activeTab,
    setActiveTab,
    firstCategory,
    activeCategory,
    availableCategories,
  } = useTabs({
    userId: deepLinkDependentId || id,
    withVitals: true,
    isProvider,
    deepLinkTab,
  });

  const activeCategoryTitle = get(activeCategory, 'title', '');

  const {
    data,
    pending,
    loadMore,
    withPagination,
    handleSelectTab,
    succeed = false,
  } = useDataListEntity({
    activeTab,
    setActiveTab,
    userId: deepLinkDependentId || id,
    firstCategoryType: firstCategory.categoryType,
  });

  const getDataAction = useActionFilters(activeCardType, false);
  const dispatch = useDispatch();

  const handleBackLogic = () => {
    clearHistoricData();
    toggleDetailsView(false);
    dispatch(getDataAction.clear());
  };

  useEffect(() => {
    getFeatureConfig();
  }, [id]);

  const typeWithAddForm = isWeightType || isHeightType || isSaturationType;

  const handleOpenRefillsModal = useCallback(params => {
    dispatch({
      type: popupConsts.SHOW,
      payload: {
        contentType: UI_POPUP_TYPES.MEDICATIONS_REFILLS,
        contentOptions: params,
      },
    });
  }, []);

  const mainTitle = useMemo(() => {
    switch (activeTab) {
      case MEDICATIONS: {
        return (
          <div className={styles.contentTitle}>
            <div className={styles.titleLeft}>
              <div>{activeCategoryTitle}</div>
              {refillsHistory.length > 0 && (
                <div
                  className={styles.refillHistory}
                  onClick={() => handleOpenRefillsModal({ viewHistory: true })}
                >
                  {formatMessage({
                    id: 'patient.portal.refillHistory',
                  })}
                </div>
              )}
            </div>
            {isFeatureToggleOn && (
              <Button
                color="primary"
                onClick={() => handleOpenRefillsModal({ viewHistory: false })}
              >
                {formatMessage({ id: 'patient.portal.requestRefills' })}
              </Button>
            )}
          </div>
        );
      }
      default: {
        return (
          <div className={styles.contentTitle}>
            <div className={styles.titleLeft}>
              <div>{activeCategoryTitle}</div>
            </div>
          </div>
        );
      }
    }
  }, [activeCategoryTitle, activeTab, refillsHistory]);

  const handleViewAddDataForm = (type, contextMainUser, modalMode) => {
    if (isSaturationType)
      return <SaturationInfoForm formatMessage={formatMessage} />;

    if (contextMainUser) {
      switch (type) {
        case WEIGHT:
          return (
            <AddWeightForm
              formatMessage={formatMessage}
              modalMode={modalMode}
              isWeightInKg={isWeightInKg}
            />
          );
        case HEIGHT:
          return (
            <AddHeightForm
              formatMessage={formatMessage}
              handleRedirect={handleBackLogic}
              isHeightInMeters={isHeightInMeters}
              modalMode={modalMode}
            />
          );
        case BMI:
          return (
            <AddBodyInfoForm
              formatMessage={formatMessage}
              modalMode={modalMode}
              isWeightInKg={isWeightInKg}
              isHeightInMeters={isHeightInMeters}
            />
          );
      }
    }
  };

  if (!availableCategories.length && !availableCategoriesPending) {
    return (
      <div className={styles.notAvailable}>
        <Icon
          size={60}
          name="search-list"
          className={styles.notAvailableIcon}
        />
        <div>{formatMessage({ id: 'healthboard.notAvailable' })}</div>
      </div>
    );
  }

  return (
    <div className={cn(styles.root, { [styles.rootUserContext]: !isProvider })}>
      <MetaHelmet title="pages.title.healthboard" />

      {isDetailsView && (
        <Popup
          strictClose
          contentNoPadding
          opened={opened}
          onClose={toggleOpenModal}
          withHeading={false}
          disableFillBackground
          mobileFullScreen={false}
          overflowVisible
        >
          <div className={styles.modalRoot}>
            <div className={styles.modalContent}>
              <div className={styles.coverHeader} onClick={handleBackLogic} />
              <BackButton
                onBack={handleBackLogic}
                className={styles.backButton}
                minWidth={0}
              />
              <VitalsActivity
                formatMessage={formatMessage}
                isDetailsView={isDetailsView}
                toggleDetailsView={toggleDetailsView}
                toggleOpenModal={toggleOpenModal}
                contextOf={displayableName}
                activeCardType={activeCardType}
                setActiveCardType={setActiveCardType}
                isWeightInKg={isWeightInKg}
                isHeightInMeters={isHeightInMeters}
                isTemperatureInCelsius={isTemperatureInCelsius}
                isMilligramsPerDeciliter={isMilligramsPerDeciliter}
                mainUser={mainUser}
                userId={id}
                hideTitle={false}
              />
              {isTimeScalableChart && isAvailableData ? (
                <TimeScalableChart
                  activeCardType={activeCardType}
                  isTemperatureInCelsius={isTemperatureInCelsius}
                />
              ) : (
                <Fragment>
                  {isAvailableData ? (
                    <Chart
                      formatMessage={formatMessage}
                      type={activeCardType}
                      activeFilter={activeFilter}
                      setActiveFilter={setActiveFilter}
                      isWeightInKg={isWeightInKg}
                      isHeightInMeters={isHeightInMeters}
                      isMilligramsPerDeciliter={isMilligramsPerDeciliter}
                      mainUser={mainUser}
                    />
                  ) : (
                    <EmptyState
                      formatMessage={formatMessage}
                      type={activeCardType}
                      isProvider={isProvider}
                      mainUser={mainUser}
                      userId={id}
                    />
                  )}
                  {isAvailableData && (
                    <div
                      className={cn(styles.dataHistoricContentWrapper, {
                        [styles.isHeightType]: isHeightType,
                      })}
                    >
                      {!isHeightType && (
                        <DataHistoricList
                          formatMessage={formatMessage}
                          type={activeCardType}
                          isWeightInKg={isWeightInKg}
                          isMilligramsPerDeciliter={isMilligramsPerDeciliter}
                          userId={id}
                          mainUSer={mainUser}
                        />
                      )}
                      {typeWithAddForm ? (
                        handleViewAddDataForm(activeCardType, mainUser)
                      ) : (
                        <div className={styles.withOutAddForm} />
                      )}
                    </div>
                  )}
                  <AddDataModal
                    component={handleViewAddDataForm(
                      activeCardType,
                      mainUser,
                      true,
                    )}
                  />
                </Fragment>
              )}
            </div>
          </div>
        </Popup>
      )}
      {!isDetailsView && (
        <Fragment>
          <div className={cn(styles.navigation)}>
            <MediaQuery maxWidth={BREAKPOINT}>
              {isMobile => (
                <Drawer opened={isMobile ? visible : desktopVisible}>
                  <Navigation
                    userId={Number(id)}
                    tabs={tabs}
                    handleSelectTab={handleSelectTab}
                    activeTab={activeTab}
                    setTargetUserId={setTargetUserId}
                  />
                </Drawer>
              )}
            </MediaQuery>
            <ContentArea className={styles.contentWrapper}>
              <Fragment>
                <MediaQuery maxWidth={BREAKPOINT}>
                  <MobileNavbar openContext="arrow-down" />
                </MediaQuery>
                <div className={styles.patientContent}>
                  {mainTitle}
                  {activeTab === VITALS_AND_ACTIVITY ? (
                    <VitalsActivity
                      formatMessage={formatMessage}
                      isDetailsView={isDetailsView}
                      toggleOpenModal={toggleOpenModal}
                      toggleDetailsView={toggleDetailsView}
                      contextOf={displayableName}
                      activeCardType={activeCardType}
                      setActiveCardType={setActiveCardType}
                      isWeightInKg={isWeightInKg}
                      isHeightInMeters={isHeightInMeters}
                      isTemperatureInCelsius={isTemperatureInCelsius}
                      isMilligramsPerDeciliter={isMilligramsPerDeciliter}
                      mainUser={mainUser}
                      userId={id}
                      hideTitle
                    />
                  ) : (
                    <DataListWrapper
                      userId={id}
                      data={data}
                      pending={pending}
                      succeed={succeed}
                      loadMore={loadMore}
                      activeTab={activeTab}
                      formatMessage={formatMessage}
                      category={activeCategoryTitle}
                      withPagination={withPagination}
                    />
                  )}
                </div>
              </Fragment>
            </ContentArea>
          </div>
        </Fragment>
      )}
      <SingleDayGrafModal />
      <SinglePointValueModal />
    </div>
  );
}

PatientHealthboard.propTypes = {
  profile: PropTypes.object,
  intl: PropTypes.object,
  isProvider: PropTypes.bool,
  vitalsActivityData: PropTypes.array,
  activeCardType: PropTypes.string,
  setActiveCardType: PropTypes.func,
  activeFilter: PropTypes.string,
  setActiveFilter: PropTypes.func,
  clearHistoricData: PropTypes.func,
  drawerVisibility: PropTypes.object,
  setTargetUserId: PropTypes.func,
  profileTargetUser: PropTypes.object,
  deepLinkTab: PropTypes.string,
  deepLinkDependentId: PropTypes.string,
  availableCategoriesPending: PropTypes.bool,
  refillsHistory: PropTypes.array,
};

export default memo(enhance(PatientHealthboard));



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/index.jsx